import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { ui } from "@src/interfaces/interfaces";
import {
    RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult,
} from "@src/utilities/apis/wbs/model";
import { rateIfCareClubSold, roundToTwoDecimalPlaces } from "@src/utilities/general";

/**
 * Handles the logic for applying the Combo Promo to the Care Plan.
 * Updates various UI states to reflect the application of the promo.
 */
const ccPromo = () => {
    // Retrieve the current Care Plan reference from the global UI state
    const carePlanReference = ui.CarePlanReference;

    // If a Care Plan reference exists, do not proceed with applying the promo
    if (carePlanReference) return;

    // Update UI state to navigate to the Care Plan step and set active tabs
    ui.ActiveStep = 2;
    ui.StepThreeActiveTab = "care plan";
    ui.CarePlanActiveTab = 0;
    ui.CarePlanOffered = true;
    ui.CarePlanPlanDetailsActiveSideTab = 0;
};

/**
 * AppliedBadge Component
 * Renders an "APPLIED" badge over the RateBadge when in a discounted state.
 *
 * @returns {React.FC} The AppliedBadge component.
 */
const AppliedBadge: React.FC = () => (
    <Typography
        variant="caption"
        sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-25deg)",
            bgcolor: "green",
            color: "white",
            fontWeight: "bold",
            fontSize: "1.35rem",
            px: 2,
            py: 0.5,
            zIndex: 1,
            borderRadius: 1,
            opacity: 0.8,
        }}
    >
        APPLIED
    </Typography>
);

/**
 * Props interface for the Circle component.
 */
interface CircleProps {
    /**
     * The number to display inside the circle.
     */
    number: number;
}
/**
 * Circle Component with a number inside + VAT. Should be shown in right corner of the AppliedBadge.
 * 
 * @param {CircleProps} props - The props for the component.
 * @returns {React.FC} The Circle component.
 */
const Circle: React.FC<CircleProps> = ({ number }) => (
    <Typography
        variant="h6"
        sx={{
            display: "flex",
            position: "absolute",
            top: "2%",
            right: "2%",
            justifyContent: "center",
            alignItems: "center",
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: "green",
            color: "white",
            fontWeight: "bold",
            fontSize: ".9rem",
            textAlign: "center",
        }}
    >
        £{number} +VAT
    </Typography>
);

/**
 * Props interface for the RateBadge component.
 */
interface RateBadgeProps {
    /**
     * Indicates whether the badge is in a discounted state.
     */
    discounted: boolean;

    /**
     * The charge details associated with the booking result.
     */
    charge: RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult;
}

/**
 * RateBadge Component
 * Displays a rate badge with conditional styling and behavior based on discount status.
 *
 * @param {RateBadgeProps} props - The props for the component.
 * @param {boolean} props.discounted - Indicates if the badge is discounted.
 * @param {RightioWBSAPIModelsSPSpRFTrackFastJobsGetChargesBookingResult} props.charge - The charge details.
 * @returns {React.FC} The RateBadge component.
 */
export const RateBadge: React.FC<RateBadgeProps> = ({
    discounted,
    charge,
}) => {
    /**
     * Determines the display rate based on whether the Care Club is sold.
     * Utilizes a utility function to calculate the appropriate rate.
     *
     * @type {number}
     */
    const displayRate = useMemo(() => {
        return rateIfCareClubSold(charge?.rate ?? 0);
    }, [charge]);

    /**
     * Handles the click event on the RateBadge.
     * Applies the Combo Promo if the badge is not in a discounted state.
     */
    const handleClick = () => {
        if (!discounted) {
            ccPromo();
        }
    };

    /**
     * Determines the styles for the RateBadge based on the discount status.
     * Uses Material-UI's `sx` prop for dynamic styling.
     *
     * @type {object}
     */
    const badgeStyles = useMemo(
        () => ({
            ml: 1,
            mr: 1,
            p: 1,
            borderRadius: 2.5,
            border: 1,
            borderColor: discounted ? "gray" : "blue",
            borderStyle: "solid",
            bgcolor: discounted ? "#e0e0e0" : "#d8ebff52",
            position: "relative" as const,
            cursor: discounted ? "not-allowed" : "pointer",
            opacity: discounted ? 0.6 : 1,
        }),
        [discounted]
    );

    /**
     * Determines the text to display based on the discount status.
     * Memoized to prevent unnecessary recalculations.
     *
     * @type {React.ReactNode | string}
     */
    const description = useMemo(() => {
        return !discounted
            ? "Discounted Rate if CC Plan Sold!"
            : "Click here to start filling CC plan options!";
    }, [discounted]);

    return (
        <Typography
            variant="h6"
            sx={badgeStyles}
            onClick={!discounted ? handleClick : undefined}
        >
            {/* Render the "APPLIED" badge if the badge is in a discounted state */}
            {discounted && <AppliedBadge />}
            {discounted && charge && charge.rate && <Circle number={roundToTwoDecimalPlaces(charge.rate - charge.rate * 0.1)} />}

            {/* Display the rate with VAT */}
            <strong>Rate: {displayRate} + VAT</strong>

            {/* Display the description based on discount status */}
            <Typography
                variant="body2"
                component="div"
                sx={{ mt: 0.5, fontStyle: "italic", fontWeight: "bold" }}
            >
                {description}
            </Typography>

            {/* If not discounted, provide a prompt to start filling CC plan options */}
            {!discounted && (
                <Typography
                    variant="body2"
                    component="div"
                    sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                    Click here to start filling CC plan options!
                </Typography>
            )}
        </Typography>
    );
};
